import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Alert, Button, Card, Space, Steps, Tag, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleTwoTone,
  SwapOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useRole, type RoleId } from '@/auth/roles';
import RoleBadge from './RoleBadge';

const { Paragraph, Text } = Typography;

/**
 * Workflow wizard primitive (S2-T31 / FR-148, FR-149).
 *
 * The single component used by all eight cross-functional workflows. The
 * Sprint 2 workflows (Estimate→Cost, MR→Procurement, VO lifecycle,
 * Contract→Billing→Collection) and the Sprint 3 workflows (Lead→Award, Claim
 * lifecycle, Attendance→Payroll, Progress→Dashboard) all consume this shell.
 *
 * Contract:
 *  - `TState` — caller-defined flow-state type. The wizard owns the state and
 *    passes it to each step's render function plus a type-safe `patch` fn.
 *  - `steps` — ordered array of step definitions. Each step declares:
 *      `titleKey` — i18n key under the caller's namespace.
 *      `allowedRoles` — roles permitted to complete this step. If the active
 *        role is not in the list, a handoff prompt is shown instead of the
 *        step body, with a "Switch role to X" button that calls `setRole()`.
 *        `null` (explicit) means any role may complete. Empty array is
 *        treated as "any role" but emits a dev warning.
 *      `body` — render function that produces the step UI. Receives the
 *        current flow state and a typed `patch` function to update it.
 *      `canProceed` — optional predicate that gates the "Next" button.
 *  - `onComplete` — called when the final step's "Finish" button is pressed.
 *    Receives the final flow-state so the workflow can persist it (e.g.,
 *    VO wizard patches the mock store; Contract→Billing advances an AR
 *    invoice). Do not treat this as an async boundary — the prototype has no
 *    backend (OOS-007).
 *
 * RTL: AntD Steps direction follows `html[dir]`. Navigation button icons
 *  (ArrowLeft/ArrowRight) map to the correct visual direction because they
 *  render inside AntD buttons which respect the Provider's `direction` prop
 *  at the app root. Any bespoke arrow work would need logical-property care;
 *  we avoid that by relying on Steps + Buttons exclusively.
 *
 * Not in scope this sprint:
 *  - Saving wizard drafts to the store (OOS — wizards are ephemeral)
 *  - Branching (non-linear next-step logic). If a future workflow needs it,
 *    add a `nextStepIndex` callback — do not hack it on top of `canProceed`.
 */

export interface WorkflowStep<TState> {
  /** i18n key for the step's title, read from `namespace`. */
  titleKey: string;
  /** Optional i18n key for the one-line step description. */
  descriptionKey?: string;
  /** Roles permitted to complete this step. Pass `null` to allow any role. */
  allowedRoles: RoleId[] | null;
  /** Render function for the step body. */
  body: (ctx: WorkflowStepContext<TState>) => ReactNode;
  /** Gate for the "Next" button. Defaults to always-enabled. */
  canProceed?: (state: TState) => boolean;
}

export interface WorkflowStepContext<TState> {
  state: TState;
  patch: (partial: Partial<TState>) => void;
  /** The step's own index, 0-based. Useful for step-specific UI. */
  stepIndex: number;
}

export interface WorkflowWizardProps<TState> {
  /** i18n namespace used to resolve `titleKey` / `descriptionKey` / finish message. */
  namespace: string;
  /** Initial flow-state. */
  initialState: TState;
  /** Ordered step definitions. */
  steps: WorkflowStep<TState>[];
  /** Called when the user presses "Finish" on the last step. */
  onComplete: (finalState: TState) => void;
  /** Optional title shown above the Steps indicator. */
  titleKey?: string;
  /** Optional one-line subtitle. */
  subtitleKey?: string;
  /** Shown in the finish banner and after `onComplete` resolves. */
  completionKey?: string;
}

/**
 * Best-fit role to suggest when the active role can't run a step. Picks the
 * first entry in `allowedRoles` — matches the left-to-right ordering in the
 * sprint brief's per-workflow role-handoff chain.
 */
function suggestedRole(allowedRoles: RoleId[] | null): RoleId | null {
  if (allowedRoles === null) return null;
  if (allowedRoles.length === 0) return null;
  return allowedRoles[0] ?? null;
}

export default function WorkflowWizard<TState>({
  namespace,
  initialState,
  steps,
  onComplete,
  titleKey,
  subtitleKey,
  completionKey,
}: WorkflowWizardProps<TState>) {
  const { t } = useTranslation([namespace, 'workflows', 'common']);
  const { role, setRole } = useRole();
  const [current, setCurrent] = useState(0);
  const [state, setState] = useState<TState>(initialState);
  const [done, setDone] = useState(false);

  const patch = useCallback((partial: Partial<TState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const activeStep = steps[current];
  const allowed = useMemo<boolean>(() => {
    if (!activeStep) return false;
    if (activeStep.allowedRoles === null) return true;
    if (activeStep.allowedRoles.length === 0) return true;
    return activeStep.allowedRoles.includes(role);
  }, [activeStep, role]);

  const canProceed = useMemo<boolean>(() => {
    if (!activeStep) return false;
    if (!allowed) return false;
    if (!activeStep.canProceed) return true;
    return activeStep.canProceed(state);
  }, [activeStep, allowed, state]);

  const isLast = current === steps.length - 1;

  if (!activeStep) {
    // Defensive — steps is always non-empty per the contract, but the array
    // index type is `WorkflowStep<TState> | undefined` under strict TS.
    return null;
  }

  if (done) {
    return (
      <Card>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space size={10} align="center">
            <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 22 }} />
            <Text strong style={{ fontSize: 16 }}>
              {t('workflows:finished.title', { defaultValue: 'Workflow complete' })}
            </Text>
          </Space>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            {completionKey
              ? t(completionKey, {
                  ns: namespace,
                  defaultValue: t('workflows:finished.description', {
                    defaultValue:
                      'The wizard has posted the prototype state. Open the relevant module to see the updated record.',
                  }),
                })
              : t('workflows:finished.description', {
                  defaultValue:
                    'The wizard has posted the prototype state. Open the relevant module to see the updated record.',
                })}
          </Paragraph>
          <Space>
            <Button
              onClick={() => {
                setCurrent(0);
                setState(initialState);
                setDone(false);
              }}
            >
              {t('workflows:finished.runAgain', { defaultValue: 'Run again' })}
            </Button>
          </Space>
        </Space>
      </Card>
    );
  }

  return (
    <div>
      {(titleKey || subtitleKey) && (
        <div style={{ marginBottom: 16 }}>
          {titleKey && (
            <Text strong style={{ fontSize: 18 }}>
              {t(titleKey, { ns: namespace })}
            </Text>
          )}
          {subtitleKey && (
            <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
              {t(subtitleKey, { ns: namespace })}
            </Paragraph>
          )}
        </div>
      )}

      <Steps
        current={current}
        items={steps.map((step) => ({
          title: t(step.titleKey, { ns: namespace }),
          description: step.descriptionKey
            ? t(step.descriptionKey, { ns: namespace })
            : undefined,
        }))}
        style={{ marginBottom: 24 }}
      />

      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <StepHeader<TState>
            step={activeStep}
            stepIndex={current}
            total={steps.length}
            activeRole={role}
            namespace={namespace}
          />

          {allowed ? (
            activeStep.body({ state, patch, stepIndex: current })
          ) : (
            <HandoffPrompt
              allowedRoles={activeStep.allowedRoles ?? []}
              activeRole={role}
              suggested={suggestedRole(activeStep.allowedRoles) ?? role}
              onSwitch={(next) => setRole(next)}
            />
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              disabled={current === 0}
              onClick={() => setCurrent((i) => Math.max(0, i - 1))}
            >
              {t('workflows:nav.back', { defaultValue: 'Back' })}
            </Button>
            <div style={{ marginInlineStart: 'auto' }}>
              {isLast ? (
                <Button
                  type="primary"
                  disabled={!canProceed}
                  onClick={() => {
                    onComplete(state);
                    setDone(true);
                  }}
                >
                  {t('workflows:nav.finish', { defaultValue: 'Finish' })}
                </Button>
              ) : (
                <Button
                  type="primary"
                  disabled={!canProceed}
                  onClick={() => setCurrent((i) => Math.min(steps.length - 1, i + 1))}
                  icon={<ArrowRightOutlined />}
                  iconPosition="end"
                >
                  {t('workflows:nav.next', { defaultValue: 'Next' })}
                </Button>
              )}
            </div>
          </div>
        </Space>
      </Card>
    </div>
  );
}

interface StepHeaderProps<TState> {
  step: WorkflowStep<TState>;
  stepIndex: number;
  total: number;
  activeRole: RoleId;
  namespace: string;
}

function StepHeader<TState>({
  step,
  stepIndex,
  total,
  activeRole,
  namespace,
}: StepHeaderProps<TState>) {
  const { t } = useTranslation([namespace, 'workflows']);
  const gated = step.allowedRoles !== null && step.allowedRoles.length > 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <Text strong style={{ fontSize: 15 }}>
        {t('workflows:step.label', {
          defaultValue: 'Step {{n}} of {{total}}',
          n: stepIndex + 1,
          total,
        })}
        {' — '}
        {t(step.titleKey, { ns: namespace })}
      </Text>
      <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
        {gated && (
          <Tag icon={<UserSwitchOutlined />} color="blue">
            {t('workflows:step.gated', { defaultValue: 'Role-gated' })}
          </Tag>
        )}
        <RoleBadge role={activeRole} size="small" />
      </div>
    </div>
  );
}

interface HandoffPromptProps {
  allowedRoles: RoleId[];
  activeRole: RoleId;
  suggested: RoleId;
  onSwitch: (next: RoleId) => void;
}

function HandoffPrompt({
  allowedRoles,
  activeRole,
  suggested,
  onSwitch,
}: HandoffPromptProps) {
  const { t } = useTranslation(['workflows', 'common']);
  return (
    <Alert
      type="info"
      showIcon
      icon={<SwapOutlined />}
      message={t('workflows:handoff.title', { defaultValue: 'Role handoff required' })}
      description={
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text>
            {t('workflows:handoff.description', {
              defaultValue:
                'This step is owned by a different role. Switch persona to continue.',
            })}
          </Text>
          <Space size={6} wrap>
            <Text type="secondary">
              {t('workflows:handoff.current', { defaultValue: 'Current' })}:
            </Text>
            <RoleBadge role={activeRole} size="small" />
            <Text type="secondary" style={{ marginInlineStart: 12 }}>
              {t('workflows:handoff.allowed', { defaultValue: 'Allowed' })}:
            </Text>
            {allowedRoles.map((r) => (
              <RoleBadge key={r} role={r} size="small" />
            ))}
          </Space>
          <div>
            <Button
              type="primary"
              icon={<UserSwitchOutlined />}
              onClick={() => onSwitch(suggested)}
            >
              {t('workflows:handoff.switchTo', {
                defaultValue: 'Switch to {{role}}',
                role: t(`common:roles.${suggested}`),
              })}
            </Button>
          </div>
        </Space>
      }
    />
  );
}
